import { MAX_AUDIO_CONVERSIONS_PER_FOLDER_UPDATE } from '@listening/api-contract';
import { useCallback, useDeferredValue, useState } from 'react';
import { flattenAudioPages, useFolderPickerList } from './audio-list-hooks';

export type FolderPickerState =
  | 'loading'
  | 'failed'
  | 'empty'
  | 'no-match'
  | 'ready';

const normalizeFolderId = (folderId: string | null | undefined) =>
  folderId ?? 'root';

export const addUpToSelectionLimit = (
  selected: ReadonlySet<string>,
  audioIds: ReadonlyArray<string>,
) => {
  const next = new Set(selected);
  for (const audioId of audioIds) {
    if (next.size >= MAX_AUDIO_CONVERSIONS_PER_FOLDER_UPDATE) break;
    next.add(audioId);
  }
  return next;
};

export function useFolderPicker(folderId: string) {
  const [search, setSearch] = useState('');
  // The server matches the needle, so the needle is a query key, not a filter
  // over already-fetched pages. Deferring it keeps the field responsive and
  // lets React drop a keystroke's render before it ever starts a request.
  const needle = useDeferredValue(search.trim());
  const query = useFolderPickerList(needle);
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const items = flattenAudioPages(query.data).filter(
    (audio) => normalizeFolderId(audio.folder_id) !== folderId,
  );
  // Selections survive a needle change, so they are read from the set itself
  // rather than from the rows the current needle happens to show.
  const selectedAudioIds = [...selectedIds];
  const selectionLimitReached =
    selectedIds.size >= MAX_AUDIO_CONVERSIONS_PER_FOLDER_UPDATE;
  const allItemsSelected =
    items.length > 0 &&
    (selectionLimitReached ||
      items.every((audio) => selectedIds.has(audio.id)));

  const toggle = (audioId: string) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.delete(audioId)) return next;
      return addUpToSelectionLimit(previous, [audioId]);
    });
  };

  const toggleAll = () => {
    setSelectedIds((previous) => {
      if (!allItemsSelected) {
        return addUpToSelectionLimit(
          previous,
          items.map((audio) => audio.id),
        );
      }
      const next = new Set(previous);
      for (const audio of items) next.delete(audio.id);
      return next;
    });
  };

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const state: FolderPickerState =
    items.length > 0
      ? 'ready'
      : query.isError
        ? 'failed'
        : query.isPending || isFetchingNextPage
          ? 'loading'
          : needle === ''
            ? 'empty'
            : 'no-match';

  return {
    items,
    selectedIds,
    selectedAudioIds,
    search,
    setSearch,
    toggle,
    toggleAll,
    loadMore,
    state,
    isLoadingMore: isFetchingNextPage,
    allItemsSelected,
    selectionLimit: MAX_AUDIO_CONVERSIONS_PER_FOLDER_UPDATE,
    selectionLimitReached,
  };
}
